import type { Hex, Lines, Widget } from '../contract.js';
import { copy } from '../copy.js';
import { hexA } from '../grid/background.js';
import { editDoc } from '../state/derive.js';
import { isYou, meOf, memberOf, profileOfMember } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { PROFILE_GLOW_ALPHA } from '../tokens.js';
import { gridVals } from './grid.js';
import type { Surface, WidgetVals } from './grid.js';
import type { PersonVals, SlideClass } from './person.js';
import { toolbarVals } from './toolbar.js';
import type { ToolbarVals } from './toolbar.js';

/** The grid surface: an open post, your draft, or a profile grid. Ported from `surfaceVals` (02 §3.14–3.15; 04 §1–§2). */

export interface SurfaceVals {
  readonly show: boolean;
  readonly op: number;
  readonly surface: Surface;
  readonly editing: boolean;
  readonly color: Hex;
  /** The colour glow behind the top of a profile. */
  readonly tint: string | null;
  readonly clip: string; readonly clipTr: string; readonly slide: SlideClass;
  readonly slots: readonly { readonly x: number; readonly y: number }[];
  /** Where a dragged widget would land. */
  readonly drop: { readonly x: number; readonly y: number; readonly w: number; readonly h: number; readonly bg: string; readonly line: string } | null;
  /** The cell under dragged files. */
  readonly fileDrop: { readonly x: number; readonly y: number } | null;
  /** The composer's empty message. */
  readonly empty: boolean;
  /** A profile grid that is still loading or failed to load. */
  readonly message: { readonly text: string; readonly pulsing: boolean; readonly retry: boolean } | null;
  readonly items: readonly WidgetVals[];
  readonly toolbar: ToolbarVals | null;
}

const SLOTS = Array.from({ length: 42 }, (_, i) => ({ x: (i % 7) * 82, y: Math.floor(i / 7) * 82 }));

/** The widgets of the profile on screen: your draft while editing, the loaded profile otherwise. */
export function profileItems(st: AppState): readonly Widget[] {
  if (isYou(st, st.profileOf) && st.pe) return st.pe.items;
  return profileOfMember(st, st.profileOf)?.items ?? [];
}

export function surfaceVals(st: AppState, pv: PersonVals): SurfaceVals {
  const v = st.view, doc = editDoc(st), ed = !!doc;
  let items: readonly Widget[] = [], color: Hex = meOf(st).color, keyBase = 'x', enter: null | 'in-pre' | 'in' = null, show = false, surface: Surface = 'post';
  let lines: Lines | null | undefined = null, message: SurfaceVals['message'] = null;
  if (v === 'person') { show = pv.show; items = pv.items; color = pv.color; keyBase = `v:${st.date}:${st.person ?? ''}`; lines = pv.lines; }
  else if (v === 'compose') { show = !!st.draft; items = st.draft ? st.draft.items : []; keyBase = 'c'; lines = st.draft ? st.draft.lines : null; }
  else if (v === 'profile') {
    const own = isYou(st, st.profileOf), m = memberOf(st, st.profileOf), saved = profileOfMember(st, st.profileOf);
    show = true; surface = 'profile'; items = profileItems(st); color = own && st.pe ? st.pe.color : m.color; keyBase = `ps:${st.profileOf ?? ''}`; enter = st.pfx;
    lines = own && st.pe ? st.pe.lines : saved?.lines ?? null;
    const status = st.pe || !st.profileOf ? 'loaded' : st.profiles[st.profileOf]?.status ?? 'loading';
    if (status !== 'loaded') message = { text: status === 'loading' ? copy.profile.loading(m.name, own) : copy.profile.failed(m.name, own), pulsing: status === 'loading', retry: status === 'error' };
  }
  const g = ed ? st.pdrag : null, move = g && g.mode === 'move' ? g : null, dc = ed ? st.dropCell : null;
  const isPerson = v === 'person';
  return {
    show, op: st.lv ? 0 : 1, surface, editing: ed, color,
    tint: v === 'profile' ? hexA(color, PROFILE_GLOW_ALPHA) : null,
    clip: isPerson ? pv.clip : 'none', clipTr: isPerson ? pv.clipTr : 'none', slide: isPerson ? pv.slide : '',
    slots: SLOTS,
    drop: move ? {
      x: move.c * 82, y: move.r * 82, w: move.w * 82 - 14, h: move.h * 82 - 14,
      bg: move.ok ? 'rgba(233, 237, 245, 0.06)' : 'rgba(224, 104, 90, 0.08)', line: move.ok ? 'rgba(233, 237, 245, 0.55)' : '#e0685a',
    } : null,
    fileDrop: dc ? { x: dc.c * 82, y: dc.r * 82 } : null,
    empty: v === 'compose' && items.length === 0 && !dc,
    message,
    items: gridVals(st, items, { editing: ed, color, keyBase, enter, surface, lines }),
    toolbar: ed ? toolbarVals(st, items, color) : null,
  };
}
